import type { OCRDocumentType } from '../lib/Car.constants';
import type {
  GetCarVehicleParams,
  GetModelPropsResponse,
  GetPlateTypesParams,
  GetVehiclePropsResponse,
  GetVehicleTypeProps,
  Make,
  Model,
  ModelProp,
  ModelYear,
  PlateType,
  UploadOcrResponse,
  VehicleType,
} from '../model/Car.types';
import { BaseService, defineApi } from '@/shared/api';

class CarService extends BaseService {
  async getVehicleProps(params: GetCarVehicleParams): Promise<GetVehiclePropsResponse> {
    return await this.post('/vehicle-props', params);
  }

  async getMakes(): Promise<Make[]> {
    return await this.get('/makes');
  }

  async getModels(makeId: string): Promise<Model[]> {
    return await this.get('/models', { params: { makeId } });
  }

  async getModelProps(modelId: string): Promise<GetModelPropsResponse> {
    return await this.get(`/model-props`, { params: { modelId } });
  }

  async getVehicleTypes(modelId: string): Promise<VehicleType[]> {
    return await this.get(`/vehicle-types`, { params: { modelId } });
  }

  async getVehicleTypePropsByVehicleType(
    vehicleType: string,
  ): Promise<GetVehicleTypeProps> {
    return await this.get(`/vehicle-type-props`, { params: { vehicleType } });
  }

  async getPlateTypes(params: GetPlateTypesParams): Promise<PlateType[]> {
    return await this.get('/plate-types', { params });
  }

  async uploadOcr(file: File, istimaraType: OCRDocumentType): Promise<UploadOcrResponse> {
    const formData = new FormData();
    formData.append('ocrfile', file);
    formData.append('type', String(istimaraType));

    return await this.post('/ocr-upload', formData);
  }

  async getVin({ plateNumber, userId }: { plateNumber: string; userId: string }): Promise<string | null> {
    const { vin } = await this.post<{ vin: string | null }>('/get-vin', { plateNumber, userId });
    return vin ?? null;
  }

  async getModelYears({ modelId }: { modelId: string }): Promise<ModelYear[]> {
    return await this.get(`/model-years`, { params: { modelId } });
  }

  async getModelSeats({ modelId, modelYear }: { modelId: string; modelYear: number }): Promise<ModelProp[]> {
    return await this.get(`/model-seats`, { params: { modelId, modelYear } });
  }

  async getModelCylinders(
    { modelId, modelYear, seatsId }: { modelId: string; modelYear: number; seatsId: string },
  ): Promise<ModelProp[]> {
    return await this.get(`/model-cylinders`, { params: { modelId, modelYear, seatsId } });
  }
}

export const useCarService = defineApi(CarService, { version: 'v3' });
